import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { log } from '@/lib/logger';
import { Home, Plus, LogOut, MessageSquare, FileSignature, DollarSign, Users, Menu } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import ViewportMenuItems from '@/components/ViewportMenuItems';
import { ReportBugDialog } from '@/components/ReportBug';
import { takePhoto } from '@/hooks/useNative';
import NotificationBell from '@/components/NotificationBell';
import { BroadcastDialog } from '@/components/BroadcastDialog';
import { PayoutsBanner } from '@/components/landlord/PayoutsBanner';
import { PricingInsightsWidget } from '@/components/landlord/PricingInsightsWidget';
import { TopSharedWidget } from '@/components/landlord/TopSharedWidget';
import { LandlordPropertyCard } from '@/components/landlord/LandlordPropertyCard';
import PropertyFormDialog from '@/components/landlord/PropertyFormDialog';
import SecurityModal from '@/components/SecurityModal';
import BackButton from '@/components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandlordDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const isEmployee = user?.role === 'employee';
  const perms = user?.permissions || [];
  const can = (p) => user?.role === 'landlord' || perms.includes(p);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [showBugDialog, setShowBugDialog] = useState(false);
  const [topShared, setTopShared] = useState(null);
  const [pricingInsights, setPricingInsights] = useState(null);

  // Memoize expensive JSX computations flagged by the code-review pass
  const pricingFlaggedRows = useMemo(
    () => (pricingInsights?.properties || []).filter((p) => p.verdict === 'underpriced' || p.verdict === 'overpriced'),
    [pricingInsights]
  );
  const topSharedRows = useMemo(() => {
    const rows = topShared?.top || [];
    return rows.map((row) => ({
      ...row,
      sortedSources: Object.entries(row.sources).sort((a, b) => b[1] - a[1]),
    }));
  }, [topShared]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    city: '',
    price: '',
    property_type: 'apartment',
    bedrooms: '',
    bathrooms: '',
    area: '',
    amenities: '',
    images: '',
    discount_rate: '',
    duration_discounts: { '3months': '', '6months': '', '12months': '' },
    seasonal_discount: '',
    application_fee: ''
  });

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/my`);
      setProperties(response.data);
      setLoading(false);
    } catch (error) {
      log.error('Failed to fetch properties', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
    if (!isEmployee) {
      axios.get(`${API}/stripe/connect/status`)
        .then((res) => setPayoutStatus(res.data))
        .catch((err) => log.debug('LandlordDashboard: connect/status fetch failed', err));
    }
    axios.get(`${API}/landlord/top-shared?days=30&limit=5`)
      .then((res) => setTopShared(res.data))
      .catch((err) => log.debug('LandlordDashboard: top-shared fetch failed', err));
    axios.get(`${API}/landlord/pricing-insights`)
      .then((res) => setPricingInsights(res.data))
      .catch((err) => log.debug('LandlordDashboard: pricing-insights fetch failed', err));
  }, [fetchProperties, isEmployee]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      bedrooms: parseInt(formData.bedrooms),
      bathrooms: parseInt(formData.bathrooms),
      area: parseFloat(formData.area),
      amenities: formData.amenities.split(',').map(a => a.trim()).filter(a => a),
      images: [
        ...uploadedImages,
        ...formData.images.split(',').map(i => i.trim()).filter(i => i)
      ],
      discount_rate: parseFloat(formData.discount_rate) || 0,
      duration_discounts: {
        '3months': parseFloat(formData.duration_discounts['3months']) || 0,
        '6months': parseFloat(formData.duration_discounts['6months']) || 0,
        '12months': parseFloat(formData.duration_discounts['12months']) || 0
      },
      seasonal_discount: parseFloat(formData.seasonal_discount) || 0,
      application_fee: parseFloat(formData.application_fee) || 0
    };

    try {
      if (editingProperty) {
        await axios.put(`${API}/properties/${editingProperty.id}`, payload);
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${API}/properties`, payload);
        toast.success('Property created successfully');
      }
      
      setShowAddDialog(false);
      setEditingProperty(null);
      setUploadedImages([]);
      resetForm();
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setUploadedImages(property.images || []);
    setFormData({
      title: property.title,
      description: property.description,
      location: property.location,
      city: property.city,
      price: property.price.toString(),
      property_type: property.property_type,
      bedrooms: property.bedrooms.toString(),
      bathrooms: property.bathrooms.toString(),
      area: property.area.toString(),
      amenities: property.amenities.join(', '),
      images: '',
      discount_rate: property.discount_rate.toString(),
      duration_discounts: {
        '3months': property.duration_discounts?.['3months']?.toString() || '',
        '6months': property.duration_discounts?.['6months']?.toString() || '',
        '12months': property.duration_discounts?.['12months']?.toString() || ''
      },
      seasonal_discount: property.seasonal_discount.toString(),
      application_fee: (property.application_fee || 0).toString()
    });
    setShowAddDialog(true);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploadingImage(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const response = await axios.post(`${API}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const fullUrl = `${BACKEND_URL}${response.data.url}`;
        setUploadedImages(prev => [...prev, fullUrl]);
      }
      toast.success(`${files.length} image(s) uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeUploadedImage = (idx) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handleTakePhoto = async () => {
    setUploadingImage(true);
    try {
      const shot = await takePhoto({ source: 'camera' });
      if (!shot?.dataUrl) return;
      // Convert dataURL to Blob and upload
      const blob = await (await fetch(shot.dataUrl)).blob();
      const fd = new FormData();
      fd.append('file', blob, `photo.${shot.format || 'jpg'}`);
      const response = await axios.post(`${API}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedImages(prev => [...prev, `${BACKEND_URL}${response.data.url}`]);
      toast.success('Photo captured & uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to capture photo');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      await axios.delete(`${API}/properties/${propertyId}`);
      toast.success('Property deleted successfully');
      fetchProperties();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      city: '',
      price: '',
      property_type: 'apartment',
      bedrooms: '',
      bathrooms: '',
      area: '',
      amenities: '',
      images: '',
      discount_rate: '',
      duration_discounts: { '3months': '', '6months': '', '12months': '' },
      seasonal_discount: '',
      application_fee: ''
    });
  };

  const calculateDiscountedPrice = (property) => {
    const baseDiscount = property.discount_rate || 0;
    const maxDurationDiscount = Math.max(
      property.duration_discounts?.['3months'] || 0,
      property.duration_discounts?.['6months'] || 0,
      property.duration_discounts?.['12months'] || 0
    );
    const totalDiscount = baseDiscount + maxDurationDiscount + (property.seasonal_discount || 0);
    return property.price * (1 - totalDiscount / 100);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            <BackButton fallback="/" />
            <div className="flex items-center gap-2 sm:gap-3 min-w-0" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <Home className="w-6 h-6 sm:w-7 sm:h-7 text-primary shrink-0" />
              <span className="text-lg sm:text-2xl font-bold tracking-tight text-primary truncate">PrimePocket</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <NotificationBell />
            {can('respond_messages') && <BroadcastDialog />}
            <SecurityModal />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="landlord-menu-trigger" variant="outline" size="sm" className="rounded-full gap-2">
                  <Menu className="w-4 h-4" />
                  <span className="hidden sm:inline">Menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="landlord-menu">
                <DropdownMenuLabel className="flex flex-col items-start">
                  <span className="font-semibold">{user?.name}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {isEmployee ? `Team · ${user?.parent_landlord_name}` : user?.email}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-leases" onClick={() => navigate('/leases')}>
                  <FileSignature className="w-4 h-4 mr-2" /> Leases
                </DropdownMenuItem>
                {can('respond_messages') && (
                  <DropdownMenuItem data-testid="menu-messages" onClick={() => navigate('/messages')}>
                    <MessageSquare className="w-4 h-4 mr-2" /> Messages
                  </DropdownMenuItem>
                )}
                {can('manage_forms') && (
                  <DropdownMenuItem data-testid="menu-forms" onClick={() => navigate('/forms')}>
                    <FileSignature className="w-4 h-4 mr-2" /> Forms
                  </DropdownMenuItem>
                )}
                {can('view_earnings') && (
                  <DropdownMenuItem data-testid="menu-earnings" onClick={() => navigate('/landlord/earnings')}>
                    <DollarSign className="w-4 h-4 mr-2" /> Earnings
                  </DropdownMenuItem>
                )}
                {!isEmployee && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="menu-payouts" onClick={() => navigate('/landlord/payouts')}>
                      <DollarSign className="w-4 h-4 mr-2" /> Payouts
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-team" onClick={() => navigate('/landlord/employees')}>
                      <Users className="w-4 h-4 mr-2" /> Team
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-report-bug" onClick={() => setShowBugDialog(true)}>
                  <MessageSquare className="w-4 h-4 mr-2" /> Report a bug
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-red-600 focus:text-red-700">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
                <ViewportMenuItems />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <ReportBugDialog open={showBugDialog} onOpenChange={setShowBugDialog} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
        {/* Payouts banner — only for landlords, not employees */}
        <PayoutsBanner
          payoutStatus={payoutStatus}
          isEmployee={isEmployee}
          onSetup={() => navigate('/landlord/payouts')}
        />

        {/* Dashboard Header */}
        <div className="mb-6 sm:mb-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight">My Properties</h1>
              <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">Manage your rental listings and discounts</p>
            </div>
            <Button
              data-testid="landlord-add-property-btn"
              disabled={!can('manage_properties')}
              title={!can('manage_properties') ? 'You do not have permission to add properties' : ''}
              onClick={() => setShowAddDialog(true)}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover self-start sm:self-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </div>
        </div>

        <PropertyFormDialog
          open={showAddDialog}
          onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) {
              setEditingProperty(null);
              resetForm();
            }
          }}
          editingProperty={editingProperty}
          formData={formData}
          setFormData={setFormData}
          fileInputRef={fileInputRef}
          uploadingImage={uploadingImage}
          uploadedImages={uploadedImages}
          onUploadImages={handleImageUpload}
          onTakePhoto={handleTakePhoto}
          onRemoveImage={removeUploadedImage}
          onSubmit={handleSubmit}
        />

        {/* Pricing Insights */}
        <PricingInsightsWidget pricingInsights={pricingInsights} pricingFlaggedRows={pricingFlaggedRows} />

        {/* Top-Shared Listings */}
        <TopSharedWidget topShared={topShared} topSharedRows={topSharedRows} />

        {/* Properties Grid */}
        {loading ? (
          <div className="text-center py-20">Loading properties...</div>
        ) : properties.length === 0 ? (
          <Card className="p-20 text-center border-2 border-dashed">
            <Home className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium mb-2">No Properties Yet</h3>
            <p className="text-muted-foreground mb-6">Start by adding your first property listing</p>
            <Button
              data-testid="empty-add-property-btn"
              onClick={() => setShowAddDialog(true)}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Property
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {properties.map((property) => (
              <LandlordPropertyCard
                key={property.id}
                property={property}
                discountedPrice={calculateDiscountedPrice(property)}
                canManage={can('manage_properties')}
                onEdit={() => handleEdit(property)}
                onDelete={() => handleDelete(property.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
